import React from "react";
import styles from './CohortTimeframe.module.scss'
import {CircleIcon} from "./CircleIcon";
import {Col, Row} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {getCurrent} from "../../reducers/util";
import {changeQueryPeriod} from "../../containers/CohortBuilder/cohortBuilderSlice";
import {LookBackPeriod, LookBackPeriodInput} from "./LookBackPeriod";
import {calculateObservationalPeriodMonths, periodMonthsSet} from "./utils";
import {FollowUpPeriod, FollowUpPeriodInput} from "./FollowUpPeriod";
import { AppState } from "../../types/AppState";
import ToolTip, {COHORT_NUMBER, INDEX_PERIOD} from "../common/ToolTip";

type BreakdownProps = {
    response?: {[key: string]: any}
}

type FormattedDate = {
    month: string
    year: string
}

type FormattedDates = {
    periodStart: FormattedDate|undefined
    periodEnd: FormattedDate|undefined
}

type IndexPeriodProps = {
    response?: {[key: string]: any}
    lookBackMonths: number
    observationalPeriodMonths: number
    followUpMonths: number
}

function periodDateStringToDate(date: string): Date {
    return new Date(`${date.split("/")[1]}-${date.split('/')[0]}-01`)
}

function formatMMYYYYDate(date?: string|Date): FormattedDate | undefined {
    if (date === undefined) {
        return undefined
    }

    const formatter = new Intl.DateTimeFormat('en', {month: 'short'})
    const asDate = date instanceof Date ? date : periodDateStringToDate(date)

    return {month: formatter.format(asDate), year: asDate.getFullYear().toString()}
}

function formatPeriodDates(fromDate: string, toDate: string): FormattedDates {
    const formattedFromDate = formatMMYYYYDate(fromDate)
    const formattedToDate = formatMMYYYYDate(toDate)

    return {periodStart: formattedFromDate, periodEnd: formattedToDate}
}

function calculateIndexPeriodStartDate(lookBackMonths: number, observationalPeriodStartDate?: string): FormattedDate | undefined {
    if (observationalPeriodStartDate === undefined) {
        return undefined
    }

    const asDate = periodDateStringToDate(observationalPeriodStartDate)

    const indexPeriodStartDate = new Date(asDate.setMonth(asDate.getMonth() + lookBackMonths))
    return formatMMYYYYDate(indexPeriodStartDate)
}

function calculateIndexPeriodEndDate(followUpMonths: number, observationalPeriodEndDate?: string): FormattedDate | undefined {
    if (observationalPeriodEndDate === undefined) {
        return undefined
    }

    const asDate = periodDateStringToDate(observationalPeriodEndDate)

    const indexPeriodEndDate = new Date(asDate.setMonth(asDate.getMonth() - followUpMonths))
    return formatMMYYYYDate(indexPeriodEndDate)
}



const ObservationalPeriodStart = ({periodStart}: {periodStart?: FormattedDate}) => {
    if (periodStart === undefined) {
        return null;
    }

    return <span className={styles.observationalPeriodFramingDateStart}>
        {periodStart.month}<br/>
        {periodStart.year}
    </span>
}

const ObservationalPeriodEnd = ({periodEnd}: {periodEnd?: FormattedDate}) => {
    if (periodEnd === undefined) {
        return null;
    }

    return <span className={styles.observationalPeriodFramingDateEnd}>
        {periodEnd.month}<br/>
        {periodEnd.year}
    </span>
}

const IndexPeriod = (
    {response, lookBackMonths, followUpMonths, observationalPeriodMonths}: IndexPeriodProps
) => {
    const {observationalPeriodStart, observationalPeriodEnd} = useSelector((state: AppState) => {
        const currentState = getCurrent(state)
        return {
            observationalPeriodStart: currentState.query!.period.from,
            observationalPeriodEnd: currentState.query!.period.to
        }
    })

    const indexPeriodMonths = observationalPeriodMonths - lookBackMonths - followUpMonths
    const indexPeriodStart = calculateIndexPeriodStartDate(lookBackMonths, observationalPeriodStart)
    const indexPeriodEnd = calculateIndexPeriodEndDate(followUpMonths, observationalPeriodEnd)

    if (indexPeriodMonths  < 1) {
        return null
    }

    return <Col flex={indexPeriodMonths}>
        <div className={styles.indexPeriodUpper}>
            {indexPeriodStart !== undefined && periodMonthsSet(lookBackMonths) ?
                <div className={styles.indexPeriodStartDate}>{indexPeriodStart.month}<br/>{indexPeriodStart.year}</div>
                : ''
            }
            <div className={styles.indexPeriodUpperBorderLeft}/>
            <Row className={styles.indexPeriodUpperContainer}>
                <CircleIcon radius={5} outline={true} className={styles.indexPeriodCircleStart}/>
                { response?.count !== undefined ?
                    <Col className={styles.indexPeriodUpperMainContent} flex="auto">
                        <span>Cohort Number<ToolTip content={COHORT_NUMBER} /></span>
                        <span className={styles.cohortNumber} data-testid="suppressed">{Number.parseInt(response.count).toLocaleString('en-GB')}</span>
                    </Col>
                    : ''
                }
                <CircleIcon radius={5} outline={true} className={styles.indexPeriodCircleEnd}/>
            </Row>
            {indexPeriodEnd !== undefined && periodMonthsSet(followUpMonths) ?
                <div className={styles.indexPeriodEndDate}>{indexPeriodEnd.month}<br/>{indexPeriodEnd.year}</div>
                : ''
            }
            <div className={styles.indexPeriodUpperBorderRight}/>
        </div>
        <div className={styles.indexPeriodLowerContainer} data-testid='index-period'>
            <div className={styles.indexPeriodLower}>
                <span className={styles.indexPeriodSubTitle}>Index Period</span><ToolTip content={INDEX_PERIOD} /><br />
                <span className={styles.patientsInclusivityText}>(Included patients)</span><br/>
                {indexPeriodMonths > 0 ?
                    <span className={styles.indexPeriodMonths}>{indexPeriodMonths} month{indexPeriodMonths > 1 ? 's': ''}</span>
                    : ''
                }
            </div>
        </div>
    </Col>
}

export const Breakdown = ({response}: BreakdownProps) => {
    const {
        queryId, lookBackMonths, followUpMonths, observationalPeriodMonths, periodStart, periodEnd
    } = useSelector((state: AppState) => {
        const query = getCurrent(state).query!
        const { periodStart, periodEnd } = formatPeriodDates(query.period.from, query.period.to)
        return {
            queryId: query.id,
            lookBackMonths: query.period.lookback,
            followUpMonths: query.period.followup,
            observationalPeriodMonths: calculateObservationalPeriodMonths(query.period.from, query.period.to),
            periodStart,
            periodEnd
        }
    })

    const dispatch = useDispatch()
    const setLookBackMonths = (queryId: number, value: number|string|undefined) => {
        dispatch(changeQueryPeriod({ queryId, props: { lookback: value } }))
    }
    const setFollowUpMonths = (queryId: number, value: number|string|undefined) => {
        dispatch(changeQueryPeriod({ queryId, props: { followup: value } }))
    }

    return <Row className={styles.timeframeBreakdown}>
        <div className={styles.observationalPeriodArrow}/>
        <ObservationalPeriodStart periodStart={periodStart}/>
        <LookBackPeriodInput
            readOnly={response !== undefined}
            queryId={queryId}
            lookBackMonths={lookBackMonths!}
            setLookBackMonths={setLookBackMonths}
            testId='look-back-period'
        />
        <LookBackPeriod lookBackMonths={lookBackMonths!}/>
        <IndexPeriod
            response={response}
            lookBackMonths={lookBackMonths!}
            followUpMonths={followUpMonths!}
            observationalPeriodMonths={observationalPeriodMonths}
        />
        <FollowUpPeriodInput
            readOnly={response !== undefined}
            queryId={queryId}
            followUpMonths={followUpMonths!}
            setFollowUpMonths={setFollowUpMonths}
            testId='follow-up-period'
        />
        <FollowUpPeriod followUpMonths={followUpMonths!}/>
        <ObservationalPeriodEnd periodEnd={periodEnd}/>
    </Row>
}